import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authGet, authPost, authPut, authDelete } from '../../utils/auth';
import {
  ArrowLeft, Send, Eye, Copy, Check, AlertCircle, Share2, Radio, Clock,
  Calendar, Play, Settings, Link2, Zap, Twitter, Edit2, RefreshCw,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Trash2, List, BarChart2, MapPin, Building2, ImageIcon,
  Upload, Star,
} from 'lucide-react';

// Sample variables for the deterministic landscape-card preview. Same values
// the backend renderer's tests use — see backend/tests/test_card_renderer.py.
// Generic on purpose so the thumbnails read as "card design", not real data;
// the Preview dialog substitutes a real business when the admin picks one.
const SAMPLE_VARS = {
  new_listing: { name: 'Bondi Bitcoin Cafe', website: 'bondibtc.au' },
  business_awareness: { name: 'Hobart Hardware', website: 'hobarthw.com.au' },
  abib_members: { name: 'Sydney Sats Coffee', website: 'sydneysats.io' },
  newest_business: { name: 'Mount Lofty Roasters', website: 'lofty.co' },
  state_based: { state: 'NSW', region: 'Newcastle', name: 'Harbour Burgers', website: 'harburgers.com' },
  metric_growth: { new_this_week: '42', total_businesses: '29,322', weekly_growth: '1.4' },
  metric_milestone: { total_businesses: '29,322', remaining: '678', next_milestone: '30,000' },
  metric_categories: { top_category: 'Food & Drink', category_count: '8,412', top_category_tag: 'Food' },
  metric_historical_mom: { new_this_month: '184', month_name: 'May', monthly_growth: '3.1', total_businesses: '29,322' },
  metric_historical_yoy: { this_year: '2,118', last_year: '1,476', yoy_growth: '43.5', trend: 'accelerating' },
  metric_trending: { trending_name: 'Coffee on Crown', trending_views: '1,284', trending_region: 'NSW' },
  metric_regional: { state_1: 'NSW', count_1: '9,144', state_2: 'VIC', count_2: '7,201', state_3: 'QLD', count_3: '5,612' },
  metric_events: { events_this_week: '11', event_cities: 'Sydney, Melbourne, Brisbane' },
  community_stats: { total_businesses: '29,322', upcoming_events: '11', abib_members: '47' },
};
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { DEFAULT_RELAYS, PLACEHOLDERS } from '@/data/nostr';

// A secret the GET endpoint returned masked contains the bullet char. Treat it
// as "not a real value the user typed" so we never display the bullets in a
// visible input nor round-trip the mask back to the server on save. Mirrors the
// backend's `_is_real_nsec` / `_is_real_webhook` guards (see social_media.py).
const isMaskedSecret = (v) => typeof v === 'string' && v.includes('•');

const AU_STATES = ['NSW','VIC','QLD','SA','WA','TAS','NT','ACT'];
const BG_COUNTRIES = [['AU','Australia'],['US','United States'],['GB','United Kingdom'],['NZ','New Zealand'],['CA','Canada'],['DE','Germany'],['ZA','South Africa'],['SV','El Salvador'],['NL','Netherlands']];

// IANA timezone per AU state — State-Based posts are scheduled in local time,
// so their times must be displayed in the state's zone, not always Sydney.
const AU_STATE_TZ = {
  NSW: 'Australia/Sydney', VIC: 'Australia/Melbourne', QLD: 'Australia/Brisbane',
  SA: 'Australia/Adelaide', WA: 'Australia/Perth', TAS: 'Australia/Hobart',
  NT: 'Australia/Darwin', ACT: 'Australia/Sydney',
};

// Insertable placeholder tokens. Extends the shared Nostr set with every token
// the backend's substitute_placeholders() actually supports (see
// backend/routes/social_media.py) so admins can insert all of them.
const SM_PLACEHOLDERS = [
  ...PLACEHOLDERS,
  { key: '{member_category}', description: 'ABIB tier (Orange Label Sponsor / Business Chamber / Industry Member)' },
  { key: '{category}', description: 'Primary category (short label)' },
  { key: '{suburb}', description: 'Suburb' },
  { key: '{locator}', description: 'Suburb · State (smart, no orphan separators)' },
  { key: '{state}', description: 'State code (raw)' },
  { key: '{state_full}', description: 'State full name' },
  { key: '{state_code}', description: 'State abbreviation' },
  { key: '{payment_methods}', description: 'Accepted payment methods' },
  { key: '{contact}', description: 'Contact info' },
];

const SocialMediaManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [copied, setCopied] = useState(false);
  
  // Connections state
  const [connections, setConnections] = useState({
    nostr: { enabled: false, nsec: '', relays: DEFAULT_RELAYS },
    makecom: { enabled: false, webhook_url: '' }
  });
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDialog, setTemplateDialog] = useState(false);
  // Ref to the template-content textarea so placeholder tokens insert at the
  // caret rather than always appending to the end.
  const templateTextareaRef = useRef(null);
  
  // Preview state
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewBusiness, setPreviewBusiness] = useState('');
  const [previewContent, setPreviewContent] = useState('');
  const [previewCardUrl, setPreviewCardUrl] = useState('');
  // Map of template_id -> rendered card URL (sample-vars 1200x675). Lazily
  // populated when the templates list arrives.
  const [cardUrls, setCardUrls] = useState({});
  // Card-assets version (bumped server-side when backgrounds change). Used to
  // invalidate the cached template-card thumbnails. Seeded from localStorage so
  // the first render can reuse last session's cache without a /preview storm.
  const [cardAssetsVersion, setCardAssetsVersion] = useState(
    () => { try { return localStorage.getItem('ol_card_assets_version') || '0'; } catch { return '0'; } }
  );
  // Map of queued-post key -> rendered card URL ('' = tried but none). Lazily
  // populated when a calendar day is selected so each concrete queued post in
  // the day-detail can show its actual rendered card image.
  // Resolved card image URLs, keyed by cardKeyFor(post). Seeded from
  // localStorage: card URLs are content-addressed/immutable, so a URL we
  // resolved before is still valid — reuse it and skip the preview round-trip.
  const [cardCache, setCardCache] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ol_card_cache') || '{}'); }
    catch { return {}; }
  });
  // Admin-managed background images for the card renderer.
  const [backgrounds, setBackgrounds] = useState([]);
  const [bgUploading, setBgUploading] = useState(false);
  const [bgCountry, setBgCountry] = useState('AU');
  const [bgState, setBgState] = useState(null); // null = National (country-level)
  
  // Custom Post state
  const [customContent, setCustomContent] = useState('');
  const [customChannels, setCustomChannels] = useState(['nostr', 'makecom']);
  
  // Queue state
  const [queueExpanded, setQueueExpanded] = useState(true);
  // IDs of queue items currently being executed or deleted — guards against
  // double-clicks publishing a live post twice.
  const [busyQueueIds, setBusyQueueIds] = useState([]);
  // Full pending queue (calendar view) + which month/day is in focus.
  const [fullQueue, setFullQueue] = useState([]);
  // Deterministic forecast from the backend — REAL upcoming content (concrete
  // business/state/event names where known) for days beyond the queued horizon.
  // Map of 'YYYY-MM-DD' -> slots[]. Replaces the old client-side placeholder
  // projection so future days show genuine planned posts, not guesses.
  const [forecastByDay, setForecastByDay] = useState({});
  const [forecastAuto, setForecastAuto] = useState(true);
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [selectedDay, setSelectedDay] = useState(null); // 'YYYY-MM-DD' or null
  
  // Scheduler state
  const [scheduler, setScheduler] = useState({
    enabled: false,
    business_awareness: { enabled: true, next_post: null },
    abib_members: { enabled: true, next_post: null },
    state_based: {}
  });
  
  // Post logs and metrics
  const [logs, setLogs] = useState([]);
  const [logsDialog, setLogsDialog] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [metricsDialog, setMetricsDialog] = useState(false);

  // Connections + Card Backgrounds are opened via header / Templates buttons.
  const [connectionsDialog, setConnectionsDialog] = useState(false);
  const [backgroundsDialog, setBackgroundsDialog] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authGet('/auth/me');
        if (!response.data || response.data.role !== 'admin') {
          navigate('/admin/login');
          return;
        }
        fetchAll();
      } catch (error) {
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]);

  const fetchAll = async () => {
    await Promise.all([
      fetchConnections(),
      fetchTemplates(),
      fetchQueue(),
      fetchForecast(),
      fetchScheduler(),
      fetchBusinesses(),
      fetchBackgrounds(),
    ]);
    setLoading(false);
  };

  const fetchConnections = async () => {
    try {
      const response = await authGet('/admin/social-media/connections');
      if (response.data) {
        setConnections(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await authGet('/admin/social-media/templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchBackgrounds = async (country) => {
    try {
      const cc = country || bgCountry || 'AU';
      const response = await authGet(`/admin/social-media/backgrounds?country_code=${encodeURIComponent(cc)}`);
      setBackgrounds(response.data?.backgrounds || []);
    } catch (error) {
      console.error('Error fetching backgrounds:', error);
    }
  };

  const uploadBackground = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    form.append('country_code', bgCountry || 'AU');
    if (bgState) form.append('state_code', bgState);
    setBgUploading(true);
    try {
      await authPost('/admin/social-media/backgrounds', form);
      toast.success(`Uploaded ${file.name}`);
      await fetchBackgrounds();
      await refreshCardUrls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setBgUploading(false);
    }
  };

  const setPrimaryBackground = async (bgId) => {
    try {
      await authPut(`/admin/social-media/backgrounds/${bgId}/primary`);
      toast.success('Primary background updated');
      await fetchBackgrounds();
      await refreshCardUrls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not set primary');
    }
  };

  const deleteBackground = async (bgId) => {
    if (!window.confirm('Delete this background?')) return;
    try {
      await authDelete(`/admin/social-media/backgrounds/${bgId}`);
      toast.success('Deleted');
      await fetchBackgrounds();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  // Re-render the 14 template thumbnails. Used after a background change
  // so the admin immediately sees the new card output.
  const refreshCardUrls = async () => {
    if (!templates || templates.length === 0) return;
    const entries = await Promise.all(
      templates.map(async (t) => {
        // Some cards (map_meetups, event_one_week) have no SAMPLE_VARS — the
        // backend computes their content, so still request a preview with {}.
        const vars = SAMPLE_VARS[t.id] || {};
        try {
          const res = await authPost('/admin/social-media/preview', {
            template: t.template, template_id: t.id, vars, format: 'x',
          });
          return [t.id, res.data?.card_url || ''];
        } catch { return [t.id, '']; }
      })
    );
    setCardUrls(Object.fromEntries(entries));
  };

  // Refresh entry point — the calendar view reads the full pending queue plus
  // the deterministic forecast for the days beyond it.
  const fetchQueue = async () => {
    fetchFullQueue();
    fetchForecast();
  };

  const fetchFullQueue = async () => {
    try {
      const response = await authGet('/admin/social-media/queue/all');
      const q = response.data?.queue;
      setFullQueue(Array.isArray(q) ? q : []);
    } catch (error) {
      console.error('Error fetching full queue:', error);
    }
  };

  // Fetch the deterministic forecast covering the currently-viewed month. The
  // backend already excludes any (date, template) that's in the pending queue,
  // so forecast slots never overlap the concrete queued posts — just merge.
  const fetchForecast = async () => {
    try {
      // Days from today through the last day of the viewed month, clamped so we
      // always pull at least a week and never an unbounded horizon.
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const lastOfMonth = new Date(calMonth.year, calMonth.month + 1, 0);
      const span = Math.round((lastOfMonth - today) / 86400000) + 1;
      const days = Math.max(7, Math.min(45, span));
      const response = await authGet(`/admin/social-media/forecast?days=${days}`);
      const map = {};
      for (const d of response.data?.days || []) {
        if (d && d.date) map[d.date] = Array.isArray(d.slots) ? d.slots : [];
      }
      setForecastByDay(map);
      if (typeof response.data?.auto_posting === 'boolean') {
        setForecastAuto(response.data.auto_posting);
      }
      // Bust the cached card thumbnails when backgrounds have changed. The
      // localStorage card map keys on template+business (not background), so a
      // background swap would otherwise show stale thumbnails — the server
      // bumps card_assets_version on any background change.
      const cav = response.data?.card_assets_version;
      if (cav !== undefined && String(cav) !== localStorage.getItem('ol_card_assets_version')) {
        try { localStorage.removeItem('ol_card_cache'); } catch { /* ignore */ }
        try { localStorage.setItem('ol_card_assets_version', String(cav)); } catch { /* ignore */ }
        setCardCache({});
        setCardAssetsVersion(String(cav)); // triggers template-thumbnail refresh
      }
    } catch (error) {
      console.error('Error fetching forecast:', error);
    }
  };

  const fetchScheduler = async () => {
    try {
      const response = await authGet('/admin/social-media/scheduler');
      if (response.data) {
        setScheduler(response.data);
      }
    } catch (error) {
      console.error('Error fetching scheduler:', error);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const response = await authGet('/admin/businesses');
      setBusinesses(response.data || []);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    }
  };

  const fetchMetrics = async () => {
    try {
      const response = await authGet('/admin/social-media/metrics');
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };

  const sendCustomPost = async () => {
    if (!customContent.trim()) {
      toast.error('Enter some content to post');
      return;
    }
    
    setPosting(true);
    try {
      const response = await authPost('/admin/social-media/custom-post', {
        content: customContent,
        channels: customChannels
      });
      
      const results = response.data;
      const nostrSuccess = results.nostr?.success;
      const makecomSuccess = results.makecom?.success;
      
      if (nostrSuccess || makecomSuccess) {
        toast.success(`Posted to ${[nostrSuccess && 'NOSTR', makecomSuccess && 'X'].filter(Boolean).join(' & ')}`);
        setCustomContent('');
      } else {
        toast.error('Post failed - check connections');
      }
      
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    }
    setPosting(false);
  };

  const fetchLogs = async () => {
    try {
      const response = await authGet('/admin/social-media/logs?limit=30');
      setLogs(response.data?.logs || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const saveConnections = async () => {
    try {
      // Don't send a masked secret back to the server. When the webhook field
      // still holds the masked placeholder (or is blank) the user hasn't typed
      // a new URL, so omit it and let the backend keep the stored credential
      // (mirrors how the masked nsec is preserved server-side).
      const payload = { ...connections };
      if (payload.makecom) {
        payload.makecom = { ...payload.makecom };
        delete payload.makecom.webhook_url_set;
        if (!payload.makecom.webhook_url || isMaskedSecret(payload.makecom.webhook_url)) {
          delete payload.makecom.webhook_url;
        }
      }
      await authPost('/admin/social-media/connections', payload);
      toast.success('Connections saved');
    } catch (error) {
      toast.error('Failed to save connections');
    }
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      await authPut(`/admin/social-media/templates/${editingTemplate.id}`, editingTemplate);
      toast.success('Template saved');
      setTemplateDialog(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const generatePreview = async () => {
    if (!previewTemplate) return;

    try {
      const business_id = previewBusiness && previewBusiness !== 'random' ? previewBusiness : null;
      const response = await authPost('/admin/social-media/preview', {
        template: previewTemplate.template,
        template_id: previewTemplate.id,
        business_id,
        // When a business is picked the backend derives vars from it; otherwise
        // ship our sample vars so the card render still has data to display.
        vars: business_id ? undefined : SAMPLE_VARS[previewTemplate.id],
        format: 'x',
      });
      setPreviewContent(response.data?.preview || previewTemplate.template);
      setPreviewCardUrl(response.data?.card_url || '');
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  // Publish the (possibly edited) preview text + its rendered card. Routes
  // through the custom-post path on purpose: triggerTemplate re-renders
  // server-side from the template and would discard the admin's edits, so to
  // honour an edited copy we send the exact text the admin sees, plus the card.
  const postEditedPreview = async () => {
    if (!previewContent.trim()) { toast.error('Content is empty'); return; }
    const channels = (previewTemplate?.channels && previewTemplate.channels.length)
      ? previewTemplate.channels
      : ['nostr', 'makecom'];
    setPosting(true);
    try {
      await authPost('/admin/social-media/custom-post', {
        content: previewContent,
        channels,
        card_url: previewCardUrl || undefined,
      });
      toast.success('Posted');
      setPreviewDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Post failed - check connections');
    } finally {
      setPosting(false);
    }
  };

  // Stable signature of the templates list (ids + bodies). fetchTemplates()
  // always returns a fresh array, so keying the card-render effect on
  // `templates` re-fired a POST /preview for ALL ~14 templates on every save or
  // trigger. Keying on this hash means cards only re-render when content changes.
  const templatesKey = templates.map(t => `${t.id}:${t.template}`).join('|');

  // Render the sample cards whenever template bodies OR the card-assets version
  // change. Cards are content-addressed/immutable, so we persist the resolved
  // template->card_url map in localStorage keyed by a signature of
  // (template bodies + assets version). On a cache hit we reuse it and SKIP the
  // ~14-call /preview storm entirely — that storm on every page load was the
  // slowness. The browser's immutable cache then serves the PNGs instantly.
  useEffect(() => {
    if (!templates || templates.length === 0) return;
    const sig = `${templatesKey}@${cardAssetsVersion}`;
    try {
      const cached = JSON.parse(localStorage.getItem('ol_template_cards') || 'null');
      if (cached && cached.sig === sig && cached.urls) {
        setCardUrls(cached.urls);
        return; // cache hit — no preview calls
      }
    } catch { /* ignore parse errors, fall through to render */ }

    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        templates.map(async (t) => {
          // Some cards (map_meetups, event_one_week) have no SAMPLE_VARS — the
          // backend computes their content, so still request a preview with {}.
          const vars = SAMPLE_VARS[t.id] || {};
          try {
            const res = await authPost('/admin/social-media/preview', {
              template: t.template,
              template_id: t.id,
              vars,
              format: 'x',
            });
            return [t.id, res.data?.card_url || ''];
          } catch {
            return [t.id, ''];
          }
        })
      );
      if (cancelled) return;
      const urls = Object.fromEntries(entries);
      setCardUrls(urls);
      try { localStorage.setItem('ol_template_cards', JSON.stringify({ sig, urls })); } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templatesKey, cardAssetsVersion]);

  const triggerTemplate = async (templateId, businessId = null) => {
    setPosting(true);
    try {
      const response = await authPost(`/admin/social-media/templates/${templateId}/trigger`, {
        business_id: businessId
      });
      
      const results = response.data;
      const nostrSuccess = results.nostr?.success;
      const makecomSuccess = results.makecom?.success;
      
      if (nostrSuccess || makecomSuccess) {
        toast.success(`Posted to ${[nostrSuccess && 'NOSTR', makecomSuccess && 'X'].filter(Boolean).join(' & ')}`);
      } else {
        toast.error('Post failed - check connections');
      }
      
      fetchQueue();
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger template');
    }
    setPosting(false);
  };

  const executeQueuedPost = async (queueId) => {
    // Guard against a double-click publishing the same live post twice.
    if (busyQueueIds.includes(queueId)) return;
    setBusyQueueIds(prev => [...prev, queueId]);
    try {
      await authPost(`/admin/social-media/queue/${queueId}/execute`);
      toast.success('Post executed');
      fetchQueue();
      fetchLogs();
    } catch (error) {
      toast.error('Failed to execute post');
    } finally {
      setBusyQueueIds(prev => prev.filter(id => id !== queueId));
    }
  };

  const deleteQueuedPost = async (queueId) => {
    if (busyQueueIds.includes(queueId)) return;
    if (!window.confirm('Remove this post from the queue?')) return;

    setBusyQueueIds(prev => [...prev, queueId]);
    try {
      await authDelete(`/admin/social-media/queue/${queueId}`);
      toast.success('Post removed from queue');
      fetchQueue();
    } catch (error) {
      toast.error('Failed to remove post');
    } finally {
      setBusyQueueIds(prev => prev.filter(id => id !== queueId));
    }
  };

  const toggleScheduler = async (enabled) => {
    try {
      const newScheduler = { ...scheduler, enabled };
      await authPost('/admin/social-media/scheduler', newScheduler);
      setScheduler(newScheduler);
      
      if (enabled) {
        toast.success('Scheduler enabled - posts queued');
        fetchQueue();
      } else {
        toast.success('Scheduler disabled');
      }
    } catch (error) {
      toast.error('Failed to update scheduler');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  const formatDate = (isoString, state) => {
    if (!isoString) return 'Not scheduled';
    // State-Based posts are scheduled in their state's local time — display
    // them in that zone. Everything else defaults to Sydney.
    const timeZone = (state && AU_STATE_TZ[state]) || 'Australia/Sydney';
    return new Date(isoString).toLocaleString('en-AU', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // ── Calendar helpers ────────────────────────────────────────────────────
  // Posts are bucketed by their scheduled LOCAL date (state tz, else Sydney) so
  // a post shows on the day it will actually go out. Day keys are plain
  // 'YYYY-MM-DD' strings; the grid cells use the same plain-date keys so there's
  // no Date/timezone drift between buckets and cells.
  const _tzFor = (state) => (state && AU_STATE_TZ[state]) || 'Australia/Sydney';
  const _dayKey = (isoString, state) => {
    if (!isoString) return null;
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: _tzFor(state), year: 'numeric', month: '2-digit', day: '2-digit',
      }).format(new Date(isoString)); // en-CA → YYYY-MM-DD
    } catch { return null; }
  };
  const _timeOf = (isoString, state) => {
    if (!isoString) return '';
    try {
      return new Date(isoString).toLocaleTimeString('en-AU', {
        timeZone: _tzFor(state), hour: 'numeric', minute: '2-digit',
      });
    } catch { return ''; }
  };
  // Template → accent colour (mirrors the "Next Posts" legend dots).
  const _postAccent = (post) => {
    if (post.is_custom) return { dot: 'bg-indigo-500', chip: 'bg-indigo-100 text-indigo-700' };
    switch (post.template_id) {
      case 'business_awareness': return { dot: 'bg-orange-500', chip: 'bg-orange-100 text-orange-700' };
      case 'abib_members': return { dot: 'bg-amber-500', chip: 'bg-amber-100 text-amber-700' };
      case 'state_based': return { dot: 'bg-emerald-500', chip: 'bg-emerald-100 text-emerald-700' };
      case 'event_one_week': return { dot: 'bg-rose-500', chip: 'bg-rose-100 text-rose-700' };
      case 'map_meetups': return { dot: 'bg-sky-500', chip: 'bg-sky-100 text-sky-700' };
      case 'metric_events': return { dot: 'bg-violet-500', chip: 'bg-violet-100 text-violet-700' };
      default: return { dot: 'bg-slate-400', chip: 'bg-slate-100 text-slate-700' };
    }
  };

  // Group the full pending queue by local day key.
  const queueByDay = useMemo(() => {
    const map = {};
    for (const p of fullQueue) {
      const k = _dayKey(p.scheduled_time, p.state);
      if (!k) continue;
      (map[k] = map[k] || []).push(p);
    }
    // each day's posts already arrive sorted by scheduled_time from the API
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullQueue]);

  // Stable cache key for a concrete queued post's rendered card. Distinct
  // template/business/state/event combos render different cards.
  const cardKeyFor = (post) =>
    `${post.template_id}|${post.business_id || ''}|${post.state || ''}|${post.event_id || ''}`;

  // Lazily fetch the rendered card image for a single queued post. Dedupes
  // in-flight requests by writing a '' sentinel before awaiting, and leaves ''
  // on error so we never retry forever.
  const ensureCardFor = async (post) => {
    const key = cardKeyFor(post);
    if (key in cardCache) return; // already fetched or in-flight
    const tpl = templates.find(t => t.id === post.template_id);
    if (!tpl) return;
    setCardCache(prev => ({ ...prev, [key]: '' })); // sentinel to dedupe
    try {
      const res = await authPost('/admin/social-media/preview', {
        template: tpl.template,
        template_id: post.template_id,
        business_id: post.business_id || null,
        state: post.state,
        event_id: post.event_id,
        format: 'x',
      });
      const url = res.data?.card_url || '';
      setCardCache(prev => ({ ...prev, [key]: url }));
      // Persist only real URLs (content-addressed → reusable next session).
      if (url) {
        try {
          const store = JSON.parse(localStorage.getItem('ol_card_cache') || '{}');
          store[key] = url;
          localStorage.setItem('ol_card_cache', JSON.stringify(store));
        } catch { /* ignore quota/availability */ }
      }
    } catch (error) {
      setCardCache(prev => ({ ...prev, [key]: '' }));
    }
  };

  // When a day is selected, lazily fetch card images for its concrete posts —
  // both queued rows and forecast/auto slots (ABIB/State/event/Map/Roundup).
  // Skipped: planned Business Awareness (no business yet) and consolidated
  // multi-event reminders (no single event_id to render a card from).
  useEffect(() => {
    if (!selectedDay) return;
    for (const post of (queueByDay[selectedDay] || [])) {
      ensureCardFor(post);
    }
    for (const slot of (forecastByDay[selectedDay] || [])) {
      if (!slot.concrete) continue;
      if (slot.template_id === 'event_one_week' && !slot.event_id) continue;
      ensureCardFor(slot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, fullQueue, forecastByDay, templates]);

  // Build the 6-row month grid for calMonth as plain 'YYYY-MM-DD' cell keys.
  const calendarCells = useMemo(() => {
    const { year, month } = calMonth;
    const first = new Date(year, month, 1);
    const startOffset = first.getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ day: d, key });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [calMonth]);

  const _todayKey = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  // Re-pull the deterministic forecast whenever the viewed month changes so the
  // horizon always covers the month the admin is looking at.
  useEffect(() => {
    fetchForecast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calMonth]);

  const _monthLabel = new Date(calMonth.year, calMonth.month, 1)
    .toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  const shiftMonth = (delta) => {
    setSelectedDay(null);
    setCalMonth(({ year, month }) => {
      const d = new Date(year, month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };
  const goToToday = () => {
    const d = new Date();
    setCalMonth({ year: d.getFullYear(), month: d.getMonth() });
    setSelectedDay(_todayKey);
  };
  const _selectedDayLabel = selectedDay
    ? new Date(selectedDay + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })
    : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-muted rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
                <Share2 className="w-6 h-6 text-indigo-600" />
                Social Media
              </h1>
              <p className="text-sm text-muted-foreground">Post to NOSTR and X via Make.com</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => setConnectionsDialog(true)}>
            <Link2 className="w-4 h-4 mr-2" /> Connections
          </Button>
        </div>

        {/* Scheduler & Queue Section */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Scheduler & Queue
            </h2>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => { fetchMetrics(); setMetricsDialog(true); }}>
                <BarChart2 className="w-3 h-3 mr-1" /> Metrics
              </Button>
              <Button variant="outline" size="sm" onClick={() => { fetchLogs(); setLogsDialog(true); }}>
                <List className="w-3 h-3 mr-1" /> View Logs
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Auto-posting</span>
                <Switch
                  checked={scheduler.enabled}
                  onCheckedChange={toggleScheduler}
                />
                <span className={`text-xs font-medium ${scheduler.enabled ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {scheduler.enabled ? 'ON' : 'OFF'}
                </span>
              </div>
            </div>
          </div>

          {/* Next Posts Info */}
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm font-medium mb-1">
                <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                Business Awareness
              </div>
              <div className="text-xs text-muted-foreground">
                Next: {formatDate(scheduler.business_awareness?.next_post)}
              </div>
              <div className="text-xs text-muted-foreground">
                Daily 9am-12pm Sydney (random non-ABIB)
              </div>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm font-medium mb-1">
                <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                ABIB Members
              </div>
              <div className="text-xs text-muted-foreground">
                Next: {formatDate(scheduler.abib_members?.next_post)}
              </div>
              <div className="text-xs text-muted-foreground">
                Daily 12pm-3pm Sydney (random ABIB)
              </div>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm font-medium mb-1">
                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                State-Based
              </div>
              <div className="text-xs text-muted-foreground">
                {scheduler.state_based && Object.values(scheduler.state_based).filter(Boolean).length > 0
                  ? `${Object.values(scheduler.state_based).filter(Boolean).length} states queued`
                  : 'Not scheduled'}
              </div>
              <div className="text-xs text-muted-foreground">
                Daily 4-7pm Local Time (per state)
              </div>
            </div>
          </div>
          
          {/* Queue — calendar of upcoming posts on their scheduled days */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <span className="text-sm font-medium flex items-center gap-2">
                Upcoming Posts
                <span className="text-xs text-muted-foreground font-normal">({fullQueue.length} queued)</span>
              </span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => shiftMonth(-1)} aria-label="Previous month">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium w-36 text-center">{_monthLabel}</span>
                <Button variant="ghost" size="sm" onClick={() => shiftMonth(1)} aria-label="Next month">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday}>Today</Button>
                <Button variant="ghost" size="sm" onClick={fetchQueue} aria-label="Refresh queue">
                  <RefreshCw className="w-3 h-3" />
                </Button>
              </div>
            </div>

            {/* Weekday header */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="text-[10px] uppercase tracking-wide text-muted-foreground text-center py-1">{d}</div>
              ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarCells.map((cell, i) => {
                if (!cell) return <div key={`empty-${i}`} className="min-h-[64px]" />;
                const real = queueByDay[cell.key] || [];
                const proj = forecastByDay[cell.key] || [];
                const items = [...real, ...proj];
                const isToday = cell.key === _todayKey;
                const isSelected = cell.key === selectedDay;
                return (
                  <button
                    key={cell.key}
                    type="button"
                    onClick={() => setSelectedDay(isSelected ? null : cell.key)}
                    className={`min-h-[64px] text-left rounded-md border p-1 transition-colors ${isSelected ? 'border-primary ring-1 ring-primary' : 'border-border'} ${isToday ? 'bg-muted/60' : 'hover:bg-muted/40'}`}
                    aria-label={`${cell.key}${real.length ? `, ${real.length} queued` : ''}${proj.length ? `, ${proj.length} planned` : (real.length ? '' : ', no posts')}`}
                    aria-pressed={isSelected}
                  >
                    <div className={`text-xs ${isToday ? 'font-bold text-primary' : 'text-muted-foreground'}`}>{cell.day}</div>
                    <div className="mt-0.5 space-y-0.5">
                      {items.slice(0, 2).map((item, idx) => {
                        const a = _postAccent(item);
                        // Forecast slots have no scheduled_time (they aren't queued
                        // yet); real queued posts do.
                        const isForecast = !item.scheduled_time;
                        if (isForecast && item.concrete === false) {
                          // business_awareness: business chosen at fire time.
                          return (
                            <div
                              key={item.template_id ? `${item.template_id}-${idx}` : `f-${idx}`}
                              className="text-[10px] leading-tight px-1 py-0.5 rounded truncate border border-dashed border-muted-foreground/40 text-muted-foreground flex items-center gap-1"
                              title={`Planned: ${item.template_name}, ~${item.window} — business selected just before posting`}
                            >
                              <span className={`inline-block w-1.5 h-1.5 rounded-full ${a.dot} opacity-50 shrink-0`} />
                              {item.template_name}
                            </div>
                          );
                        }
                        if (isForecast) {
                          // Concrete forecast — real name known, just not queued yet.
                          const label = item.state || item.business_name || item.template_name;
                          const when = item.time || item.window;
                          return (
                            <div
                              key={item.template_id ? `${item.template_id}-${idx}` : `f-${idx}`}
                              className={`text-[10px] leading-tight px-1 py-0.5 rounded truncate flex items-center gap-1 ${a.chip}`}
                              title={`Auto · ${item.template_name}${item.business_name ? ` — ${item.business_name}` : ''}${item.state ? ` (${item.state})` : ''}${item.event_date ? ` · event ${item.event_date}` : ''}${when ? ` · ${when}` : ''}`}
                            >
                              <span className={`inline-block w-1.5 h-1.5 rounded-full ${a.dot} shrink-0`} />
                              <span className="truncate">{item.time ? `${item.time} ` : ''}{label}<span className="opacity-60"> ·auto</span></span>
                            </div>
                          );
                        }
                        return (
                          <div
                            key={item.id}
                            className={`text-[10px] leading-tight px-1 py-0.5 rounded truncate ${a.chip}`}
                            title={`${_timeOf(item.scheduled_time, item.state)} · ${item.template_name}${item.state ? ` (${item.state})` : ''}`}
                          >
                            {_timeOf(item.scheduled_time, item.state)} {item.state || item.template_name}
                          </div>
                        );
                      })}
                      {items.length > 2 && (
                        <div className="text-[10px] text-muted-foreground px-1">+{items.length - 2} more</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-2 text-[10px] text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded bg-muted border border-border" /> Queued (will post)
              </span>
              {forecastAuto && (
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded border border-border bg-emerald-100" /> Scheduled (auto — content known)
                </span>
              )}
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded border border-dashed border-muted-foreground/50" /> Planned (business chosen at post time)
              </span>
            </div>

            {/* Selected-day detail */}
            <div className="mt-4">
              {!selectedDay ? (
                <p className="text-sm text-muted-foreground text-center py-4 bg-muted/30 rounded-lg">
                  {fullQueue.length
                    ? 'Select a day to see its scheduled posts.'
                    : 'No posts scheduled. Enable the scheduler and templates to auto-queue posts.'}
                </p>
              ) : ((queueByDay[selectedDay] || []).length + (forecastByDay[selectedDay] || []).length) === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4 bg-muted/30 rounded-lg">
                  No posts scheduled for {_selectedDayLabel}.
                </p>
              ) : (
                <div className="space-y-2">
                  <div className="text-sm font-medium flex items-center justify-between gap-2">
                    <span>{_selectedDayLabel}</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      {(queueByDay[selectedDay] || []).length} queued{(forecastByDay[selectedDay] || []).length ? ` · ${(forecastByDay[selectedDay] || []).length} upcoming` : ''}
                    </span>
                  </div>
                  {(queueByDay[selectedDay] || []).map(post => {
                    const a = _postAccent(post);
                    const busy = busyQueueIds.includes(post.id);
                    return (
                      <div key={post.id} className="bg-muted/50 rounded-lg p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`w-2 h-2 rounded-full ${a.dot}`} />
                              <span className="text-sm font-semibold">{post.template_name}</span>
                              {post.state && (
                                <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                                  <MapPin className="w-2.5 h-2.5" /> {post.state}
                                </span>
                              )}
                              {post.template_enabled === false && (
                                <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-1.5 py-0.5 rounded">Template Disabled</span>
                              )}
                              {post.is_custom && (
                                <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">Custom</span>
                              )}
                            </div>

                            {post.business_name && post.business_name !== 'Custom' && (
                              <div className="text-sm text-foreground mt-1 truncate">
                                Business: <span className="font-medium">{post.business_name}</span>
                              </div>
                            )}

                            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(post.scheduled_time, post.state)}
                            </div>

                            {post.channels && (
                              <div className="flex gap-1 mt-2">
                                {post.channels.includes('nostr') && (
                                  <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                                    <Zap className="w-2.5 h-2.5" /> NOSTR
                                  </span>
                                )}
                                {post.channels.includes('makecom') && (
                                  <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-1.5 py-0.5 rounded flex items-center gap-1">
                                    <Twitter className="w-2.5 h-2.5" /> X
                                  </span>
                                )}
                              </div>
                            )}

                            {cardCache[cardKeyFor(post)] ? (
                              <img
                                src={cardCache[cardKeyFor(post)]}
                                alt="Card preview"
                                className="mt-2 rounded border border-border w-full max-w-[280px]"
                                style={{ aspectRatio: '16/9', objectFit: 'cover' }}
                                loading="lazy"
                              />
                            ) : cardCache[cardKeyFor(post)] === '' ? null : (
                              <div className="mt-2 text-[11px] text-muted-foreground">Loading card…</div>
                            )}

                            {post.content && (
                              <div className="mt-2 p-2 bg-muted rounded border border-border text-xs whitespace-pre-wrap max-h-24 overflow-y-auto">
                                {post.content}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            <Button size="sm" onClick={() => executeQueuedPost(post.id)} disabled={busy} title="Post Now" aria-label="Post now">
                              {busy ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => deleteQueuedPost(post.id)} disabled={busy} title="Remove" aria-label="Remove from queue">
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {(forecastByDay[selectedDay] || []).map((item, idx) => {
                    const a = _postAccent(item);
                    const key = item.template_id ? `${item.template_id}-${idx}` : `f-${idx}`;
                    if (item.concrete === false) {
                      // business_awareness — business chosen just before posting.
                      return (
                        <div key={key} className="rounded-lg p-3 border border-dashed border-muted-foreground/40">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`w-2 h-2 rounded-full ${a.dot} opacity-50`} />
                            <span className="text-sm font-semibold text-muted-foreground">{item.template_name}</span>
                            <span className="text-[10px] uppercase tracking-wide bg-muted text-muted-foreground px-1.5 py-0.5 rounded">Planned</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> ~{item.window} · business selected just before posting
                          </div>
                        </div>
                      );
                    }
                    // Concrete forecast — content is already known, not yet queued.
                    return (
                      <div key={key} className="rounded-lg p-3 border border-border bg-muted/30">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`w-2 h-2 rounded-full ${a.dot}`} />
                          <span className="text-sm font-semibold">{item.template_name}</span>
                          <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Auto</span>
                          {item.state && (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                              <MapPin className="w-2.5 h-2.5" /> {item.state}
                            </span>
                          )}
                          {item.member_category && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">{item.member_category}</span>
                          )}
                        </div>
                        {item.business_name && (
                          <div className="text-sm text-foreground mt-1 truncate">
                            {item.template_id === 'event_one_week' ? 'Event: ' : 'Business: '}
                            <span className="font-medium">{item.business_name}</span>
                          </div>
                        )}
                        {item.event_date && (
                          <div className="text-xs text-muted-foreground mt-1">Event date: {item.event_date}</div>
                        )}
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {item.time ? item.time : `~${item.window}`}
                        </div>
                        {cardCache[cardKeyFor(item)] ? (
                          <img
                            src={cardCache[cardKeyFor(item)]}
                            alt="Card preview"
                            className="mt-2 rounded border border-border w-full max-w-[280px]"
                            style={{ aspectRatio: '16/9', objectFit: 'cover' }}
                            loading="lazy"
                          />
                        ) : null}
                        <div className="text-[11px] text-muted-foreground mt-2 italic">
                          {item.time
                            ? 'Auto-scheduled at a fixed time (content rendered at post time)'
                            : 'Auto-scheduled · business locked in (will queue ~48h before)'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Templates Section */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
              <Edit2 className="w-5 h-5" />
              Templates
            </h2>
            <Button variant="outline" size="sm" onClick={() => setBackgroundsDialog(true)}>
              <ImageIcon className="w-4 h-4 mr-2" /> Backgrounds
            </Button>
          </div>

          {/* Business-Based Templates */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Business Spotlight Templates
            </h3>
            <div className="space-y-3">
              {templates.filter(t => !t.metric_source && t.card_category !== 'map').map(template => (
                <div key={template.id} className={`border rounded-lg p-4 ${template.enabled ? 'border-border' : 'border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-900/10'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium">{template.name}</h3>
                        {template.auto_trigger && (
                          <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded">Auto</span>
                        )}
                        {!template.enabled && (
                          <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-0.5 rounded">Disabled</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                      
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {template.channels?.includes('nostr') && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded flex items-center gap-1">
                            <Zap className="w-3 h-3" /> NOSTR
                          </span>
                        )}
                        {template.channels?.includes('makecom') && (
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded flex items-center gap-1">
                            <Twitter className="w-3 h-3" /> X
                          </span>
                        )}
                        {template.schedule && (
                          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded flex items-center gap-1">
                            <Clock className="w-3 h-3" /> 
                            {template.schedule.frequency === 'weekly' 
                              ? `${template.schedule.day_of_week || 'Mon'} `
                              : template.schedule.frequency === 'monthly'
                              ? `Day ${template.schedule.day_of_month || 1} `
                              : ''}
                            {template.schedule.start_hour}:00-{template.schedule.end_hour}:00
                            {template.schedule.timezone === 'local' ? ' Local' : ''}
                          </span>
                        )}
                        {template.filter?.state_based && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                            Per State
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2 ml-4">
                      {/* Deterministic landscape card preview (1200x675).
                          Lazily fetched once templates load — see useEffect for cardUrls. */}
                      <button
                        type="button"
                        // Explicit pixel height + inline aspectRatio so Tailwind JIT
                        // arbitrary-value compilation isn't on the critical path.
                        className="overflow-hidden rounded-md border border-border bg-muted hover:border-primary transition-colors"
                        style={{ width: 192, height: 108 }}
                        title="Preview card"
                        onClick={() => {
                          setPreviewTemplate(template);
                          setPreviewBusiness('');
                          setPreviewContent(template.template);
                          setPreviewCardUrl(cardUrls[template.id] || '');
                          setPreviewDialog(true);
                        }}
                      >
                        {cardUrls[template.id] ? (
                          <img
                            src={cardUrls[template.id]}
                            alt={`${template.name} card preview`}
                            // Eager fetch — these are above the action buttons so
                            // browsers were inconsistent about firing the lazy load.
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            <ImageIcon className="w-6 h-6" />
                          </div>
                        )}
                      </button>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setPreviewTemplate(template);
                          setPreviewBusiness('');
                          setPreviewContent(template.template);
                          setPreviewCardUrl(cardUrls[template.id] || '');
                          setPreviewDialog(true);
                        }}
                      >
                        <Eye className="w-3 h-3 mr-1" /> Preview
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingTemplate({ ...template });
                          setTemplateDialog(true);
                        }}
                      >
                        <Settings className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => triggerTemplate(template.id)}
                        disabled={posting || !template.enabled}
                      >
                        {posting ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                        Post Now
                      </Button>
                    </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Map Cards */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Map Cards
            </h3>
            <div className="space-y-3">
              {templates.filter(t => t.card_category === 'map').map(template => (
                <div key={template.id} className={`border rounded-lg p-4 ${template.enabled ? 'border-border' : 'border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-900/10'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium">{template.name}</h3>
                        {template.auto_trigger && (
                          <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded">Auto</span>
                        )}
                        {!template.enabled && (
                          <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-0.5 rounded">Disabled</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                      
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {template.channels?.includes('nostr') && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded flex items-center gap-1">
                            <Zap className="w-3 h-3" /> NOSTR
                          </span>
                        )}
                        {template.channels?.includes('makecom') && (
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded flex items-center gap-1">
                            <Twitter className="w-3 h-3" /> X
                          </span>
                        )}
                        {template.schedule && (
                          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded flex items-center gap-1">
                            <Clock className="w-3 h-3" /> 
                            {template.schedule.frequency === 'weekly' 
                              ? `${template.schedule.day_of_week || 'Mon'} `
                              : template.schedule.frequency === 'monthly'
                              ? `Day ${template.schedule.day_of_month || 1} `
                              : ''}
                            {template.schedule.start_hour}:00-{template.schedule.end_hour}:00
                            {template.schedule.timezone === 'local' ? ' Local' : ''}
                          </span>
                        )}
                        {template.filter?.state_based && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                            Per State
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2 ml-4">
                      {/* Deterministic landscape card preview (1200x675).
                          Lazily fetched once templates load — see useEffect for cardUrls. */}
                      <button
                        type="button"
                        // Explicit pixel height + inline aspectRatio so Tailwind JIT
                        // arbitrary-value compilation isn't on the critical path.
                        className="overflow-hidden rounded-md border border-border bg-muted hover:border-primary transition-colors"
                        style={{ width: 192, height: 108 }}
                        title="Preview card"
                        onClick={() => {
                          setPreviewTemplate(template);
                          setPreviewBusiness('');
                          setPreviewContent(template.template);
                          setPreviewCardUrl(cardUrls[template.id] || '');
                          setPreviewDialog(true);
                        }}
                      >
                        {cardUrls[template.id] ? (
                          <img
                            src={cardUrls[template.id]}
                            alt={`${template.name} card preview`}
                            // Eager fetch — these are above the action buttons so
                            // browsers were inconsistent about firing the lazy load.
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            <ImageIcon className="w-6 h-6" />
                          </div>
                        )}
                      </button>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setPreviewTemplate(template);
                          setPreviewBusiness('');
                          setPreviewContent(template.template);
                          setPreviewCardUrl(cardUrls[template.id] || '');
                          setPreviewDialog(true);
                        }}
                      >
                        <Eye className="w-3 h-3 mr-1" /> Preview
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingTemplate({ ...template });
                          setTemplateDialog(true);
                        }}
                      >
                        <Settings className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => triggerTemplate(template.id)}
                        disabled={posting || !template.enabled}
                      >
                        {posting ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                        Post Now
                      </Button>
                    </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Metric-Based Templates */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <BarChart2 className="w-4 h-4" /> Metric & Analytics Templates
            </h3>
            <div className="space-y-3">
              {templates.filter(t => t.metric_source).map(template => (
                <div key={template.id} className={`border rounded-lg p-4 ${template.enabled ? 'border-border bg-gradient-to-r from-blue-50/30 to-transparent' : 'border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-900/10'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium">{template.name}</h3>
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded">
                          {template.metric_source}
                        </span>
                        {!template.enabled && (
                          <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-0.5 rounded">Disabled</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                      
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {template.channels?.includes('nostr') && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded flex items-center gap-1">
                            <Zap className="w-3 h-3" /> NOSTR
                          </span>
                        )}
                        {template.channels?.includes('makecom') && (
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded flex items-center gap-1">
                            <Twitter className="w-3 h-3" /> X
                          </span>
                        )}
                        {template.schedule && (
                          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded flex items-center gap-1">
                            <Clock className="w-3 h-3" /> 
                            {template.schedule.frequency === 'weekly' 
                              ? `${template.schedule.day_of_week || 'Mon'} `
                              : template.schedule.frequency === 'monthly'
                              ? `Day ${template.schedule.day_of_month || 1} `
                              : ''}
                            {template.schedule.start_hour}:00-{template.schedule.end_hour}:00
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2 ml-4">
                      {/* Deterministic landscape card preview (1200x675).
                          Lazily fetched once templates load — see useEffect for cardUrls. */}
                      <button
                        type="button"
                        // Explicit pixel height + inline aspectRatio so Tailwind JIT
                        // arbitrary-value compilation isn't on the critical path.
                        className="overflow-hidden rounded-md border border-border bg-muted hover:border-primary transition-colors"
                        style={{ width: 192, height: 108 }}
                        title="Preview card"
                        onClick={() => {
                          setPreviewTemplate(template);
                          setPreviewBusiness('');
                          setPreviewContent(template.template);
                          setPreviewCardUrl(cardUrls[template.id] || '');
                          setPreviewDialog(true);
                        }}
                      >
                        {cardUrls[template.id] ? (
                          <img
                            src={cardUrls[template.id]}
                            alt={`${template.name} card preview`}
                            // Eager fetch — these are above the action buttons so
                            // browsers were inconsistent about firing the lazy load.
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            <ImageIcon className="w-6 h-6" />
                          </div>
                        )}
                      </button>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setPreviewTemplate(template);
                          setPreviewBusiness('');
                          setPreviewContent(template.template);
                          setPreviewCardUrl(cardUrls[template.id] || '');
                          setPreviewDialog(true);
                        }}
                      >
                        <Eye className="w-3 h-3 mr-1" /> Preview
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingTemplate({ ...template });
                          setTemplateDialog(true);
                        }}
                      >
                        <Settings className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => triggerTemplate(template.id)}
                        disabled={posting || !template.enabled}
                      >
                        {posting ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                        Post Now
                      </Button>
                    </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {templates.filter(t => t.metric_source).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No metric templates found. They will be created when the scheduler runs.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Create Custom Post Section */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <h2 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
            <Send className="w-5 h-5" />
            Create Post
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Post Content</Label>
              <Textarea
                rows={4}
                value={customContent}
                onChange={(e) => setCustomContent(e.target.value)}
                placeholder="Write your custom post here..."
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {customContent.length} characters
              </p>
            </div>
            
            <div>
              <Label className="text-sm">Post to Channels</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={customChannels.includes('nostr')}
                    onChange={(e) => setCustomChannels(
                      e.target.checked 
                        ? [...customChannels, 'nostr']
                        : customChannels.filter(c => c !== 'nostr')
                    )}
                  />
                  <Zap className="w-4 h-4 text-purple-600" /> NOSTR
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={customChannels.includes('makecom')}
                    onChange={(e) => setCustomChannels(
                      e.target.checked 
                        ? [...customChannels, 'makecom']
                        : customChannels.filter(c => c !== 'makecom')
                    )}
                  />
                  <Twitter className="w-4 h-4 text-blue-500" /> X (Make.com)
                </label>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button
                onClick={sendCustomPost}
                disabled={posting || !customContent.trim() || customChannels.length === 0}
              >
                {posting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Post Now
              </Button>
            </div>
          </div>
        </div>

        {/* Edit Template Dialog */}
        <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Template: {editingTemplate?.name}</DialogTitle>
            </DialogHeader>
            
            {editingTemplate && (
              <div className="space-y-4 pt-2">
                <div className="flex items-center justify-between">
                  <Label>Enabled</Label>
                  <Switch
                    checked={editingTemplate.enabled}
                    onCheckedChange={(checked) => setEditingTemplate({ ...editingTemplate, enabled: checked })}
                  />
                </div>
                
                <div>
                  <Label>Template Content</Label>
                  <Textarea
                    ref={templateTextareaRef}
                    rows={5}
                    value={editingTemplate.template || ''}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, template: e.target.value })}
                    className="font-mono text-sm mt-1"
                  />
                  <div className="flex flex-wrap gap-1 mt-2">
                    {SM_PLACEHOLDERS.map(p => (
                      <button
                        key={p.key}
                        onClick={() => {
                          const ta = templateTextareaRef.current;
                          const text = editingTemplate.template || '';
                          // Insert at the caret when we can read it; otherwise
                          // fall back to appending at the end.
                          if (ta && typeof ta.selectionStart === 'number') {
                            const start = ta.selectionStart;
                            const end = ta.selectionEnd;
                            const next = text.slice(0, start) + p.key + text.slice(end);
                            setEditingTemplate({ ...editingTemplate, template: next });
                            const caret = start + p.key.length;
                            requestAnimationFrame(() => {
                              try {
                                ta.focus();
                                ta.setSelectionRange(caret, caret);
                              } catch { /* ignore */ }
                            });
                          } else {
                            setEditingTemplate({
                              ...editingTemplate,
                              template: (text ? text + ' ' : '') + p.key,
                            });
                          }
                        }}
                        className="text-xs bg-muted hover:bg-accent px-2 py-0.5 rounded"
                        title={p.description}
                      >
                        {p.key}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Schedule Configuration */}
                {editingTemplate.schedule && !editingTemplate.auto_trigger && (
                  <div className="border border-border rounded-lg p-3 bg-muted/30">
                    <Label className="text-sm font-medium mb-2 block">Schedule Configuration</Label>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Start Hour</Label>
                        <Select 
                          value={String(editingTemplate.schedule?.start_hour || 9)}
                          onValueChange={(val) => setEditingTemplate({
                            ...editingTemplate,
                            schedule: { ...editingTemplate.schedule, start_hour: parseInt(val) }
                          })}
                        >
                          <SelectTrigger className="bg-background mt-1 h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 24 }, (_, i) => (
                              <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label className="text-xs">End Hour</Label>
                        <Select 
                          value={String(editingTemplate.schedule?.end_hour || 17)}
                          onValueChange={(val) => setEditingTemplate({
                            ...editingTemplate,
                            schedule: { ...editingTemplate.schedule, end_hour: parseInt(val) }
                          })}
                        >
                          <SelectTrigger className="bg-background mt-1 h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 24 }, (_, i) => (
                              <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <Label className="text-xs">Frequency</Label>
                        <Select 
                          value={editingTemplate.schedule?.frequency || 'daily'}
                          onValueChange={(val) => setEditingTemplate({
                            ...editingTemplate,
                            schedule: { ...editingTemplate.schedule, frequency: val }
                          })}
                        >
                          <SelectTrigger className="bg-background mt-1 h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {editingTemplate.schedule?.frequency === 'weekly' && (
                        <div>
                          <Label className="text-xs">Day of Week</Label>
                          <Select 
                            value={editingTemplate.schedule?.day_of_week || 'monday'}
                            onValueChange={(val) => setEditingTemplate({
                              ...editingTemplate,
                              schedule: { ...editingTemplate.schedule, day_of_week: val }
                            })}
                          >
                            <SelectTrigger className="bg-background mt-1 h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="monday">Monday</SelectItem>
                              <SelectItem value="tuesday">Tuesday</SelectItem>
                              <SelectItem value="wednesday">Wednesday</SelectItem>
                              <SelectItem value="thursday">Thursday</SelectItem>
                              <SelectItem value="friday">Friday</SelectItem>
                              <SelectItem value="saturday">Saturday</SelectItem>
                              <SelectItem value="sunday">Sunday</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {editingTemplate.schedule?.frequency === 'monthly' && (
                        <div>
                          <Label className="text-xs">Day of Month</Label>
                          <Select 
                            value={String(editingTemplate.schedule?.day_of_month || 1)}
                            onValueChange={(val) => setEditingTemplate({
                              ...editingTemplate,
                              schedule: { ...editingTemplate.schedule, day_of_month: parseInt(val) }
                            })}
                          >
                            <SelectTrigger className="bg-background mt-1 h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 28 }, (_, i) => (
                                <SelectItem key={i+1} value={String(i+1)}>{i+1}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-2">
                      Timezone: {editingTemplate.schedule?.timezone === 'local' ? 'Local state time' : editingTemplate.schedule?.timezone || 'Australia/Sydney'}
                    </p>
                  </div>
                )}
                
                <div>
                  <Label>Post to Channels</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editingTemplate.channels?.includes('nostr')}
                        onChange={(e) => {
                          const channels = editingTemplate.channels || [];
                          setEditingTemplate({
                            ...editingTemplate,
                            channels: e.target.checked
                              ? [...channels, 'nostr']
                              : channels.filter(c => c !== 'nostr')
                          });
                        }}
                      />
                      <Zap className="w-4 h-4 text-purple-600" /> NOSTR
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editingTemplate.channels?.includes('makecom')}
                        onChange={(e) => {
                          const channels = editingTemplate.channels || [];
                          setEditingTemplate({
                            ...editingTemplate,
                            channels: e.target.checked
                              ? [...channels, 'makecom']
                              : channels.filter(c => c !== 'makecom')
                          });
                        }}
                      />
                      <Twitter className="w-4 h-4 text-blue-500" /> X (Make.com)
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setTemplateDialog(false)}>Cancel</Button>
                  <Button onClick={saveTemplate}>Save Template</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Preview: {previewTemplate?.name}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-2">
              <div>
                <Label>Select Business (optional)</Label>
                <Select value={previewBusiness} onValueChange={setPreviewBusiness}>
                  <SelectTrigger className="bg-background mt-1">
                    <SelectValue placeholder="Random business will be selected" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="random">Random</SelectItem>
                    {businesses.filter(b => b.approved).map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.business_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button variant="outline" onClick={generatePreview} className="w-full">
                <Eye className="w-4 h-4 mr-2" /> Generate Preview
              </Button>
              
              {previewCardUrl && (
                <div className="rounded-lg overflow-hidden border border-border">
                  <img
                    src={previewCardUrl}
                    alt="Card preview (1200x675)"
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                </div>
              )}

              {previewContent && (
                <div className="bg-muted rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm">Text (editable — your edits are what gets posted):</Label>
                    <button onClick={() => copyToClipboard(previewContent)} className="p-1 hover:bg-accent rounded" title="Copy">
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <Textarea
                    rows={6}
                    value={previewContent}
                    onChange={(e) => setPreviewContent(e.target.value)}
                    className="text-sm bg-background"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Edit the copy above, then Post Now to publish this exact text with the card image. Generate Preview overwrites edits with a fresh render.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setPreviewDialog(false)}>Close</Button>
                <Button onClick={postEditedPreview} disabled={posting || !previewContent.trim()}>
                  <Send className="w-4 h-4 mr-2" /> {posting ? 'Posting…' : 'Post Now'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Logs Dialog */}
        <Dialog open={logsDialog} onOpenChange={setLogsDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Post History</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-3 pt-2">
              {logs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No posts yet</p>
              ) : (
                logs.map(log => (
                  <div key={log.id} className="border border-border rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{log.business_name || 'Custom'}</span>
                          {log.auto_triggered && (
                            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded">Auto</span>
                          )}
                          {log.is_custom && (
                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">Custom</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {new Date(log.timestamp).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {log.results?.nostr?.success && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">NOSTR ✓</span>
                        )}
                        {log.results?.makecom?.success && (
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded">X ✓</span>
                        )}
                        {!log.results?.nostr?.success && log.channels?.includes('nostr') && (
                          <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-0.5 rounded">NOSTR ✗</span>
                        )}
                        {!log.results?.makecom?.success && log.channels?.includes('makecom') && (
                          <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-0.5 rounded">X ✗</span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{log.content}</p>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Metrics Dialog */}
        <Dialog open={metricsDialog} onOpenChange={setMetricsDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Post Metrics & Distribution</DialogTitle>
            </DialogHeader>
            
            {metrics ? (
              <div className="space-y-4 pt-2">
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold">{metrics.total_businesses}</div>
                    <div className="text-xs text-muted-foreground">Total Businesses</div>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-600">{metrics.businesses_posted}</div>
                    <div className="text-xs text-muted-foreground">Posted At Least Once</div>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-amber-600">{metrics.businesses_not_posted}</div>
                    <div className="text-xs text-muted-foreground">Never Posted</div>
                  </div>
                </div>
                
                {/* Distribution Stats */}
                <div className="bg-muted/30 rounded-lg p-4">
                  <h4 className="font-medium text-sm mb-2">Fair Distribution</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Avg posts/business:</span>
                      <span className="ml-2 font-medium">{metrics.average_posts_per_business}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Max posts:</span>
                      <span className="ml-2 font-medium">{metrics.max_posts}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Min posts:</span>
                      <span className="ml-2 font-medium">{metrics.min_posts}</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    The scheduler ensures fair distribution - businesses with 0 posts get priority.
                  </p>
                </div>
                
                {/* Top Posted Businesses */}
                <div>
                  <h4 className="font-medium text-sm mb-2">Most Posted Businesses</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {metrics.distribution?.map((item, idx) => (
                      <div key={item._id} className="flex items-center justify-between bg-muted/50 rounded p-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-6">{idx + 1}.</span>
                          <span className="text-sm">{item.business_name || item._id}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground">
                            Last: {item.last_posted ? new Date(item.last_posted).toLocaleDateString() : 'N/A'}
                          </span>
                          <span className="text-sm font-medium bg-primary/10 text-primary px-2 py-0.5 rounded">
                            {item.post_count} posts
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">Loading metrics...</div>
            )}
          </DialogContent>
        </Dialog>
        {/* Connections Dialog */}
        <Dialog open={connectionsDialog} onOpenChange={setConnectionsDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Connections</DialogTitle>
            </DialogHeader>
          <div className="grid md:grid-cols-2 gap-6">
            {/* NOSTR Connection */}
            <div className={`border rounded-lg p-4 ${connections.nostr?.enabled ? 'border-purple-300 bg-purple-50/50' : 'border-border'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-600" />
                  <span className="font-medium">NOSTR</span>
                </div>
                <Switch
                  checked={connections.nostr?.enabled}
                  onCheckedChange={(checked) => setConnections({
                    ...connections,
                    nostr: { ...connections.nostr, enabled: checked }
                  })}
                />
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Private Key (nsec)</Label>
                  <Input
                    type="password"
                    value={connections.nostr?.nsec || ''}
                    onChange={(e) => setConnections({
                      ...connections,
                      nostr: { ...connections.nostr, nsec: e.target.value }
                    })}
                    placeholder="nsec1..."
                    className="font-mono text-sm mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm">Relays ({connections.nostr?.relays?.length || 5})</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(connections.nostr?.relays || DEFAULT_RELAYS).slice(0, 3).map((relay, idx) => (
                      <span key={idx} className="text-xs bg-muted px-2 py-0.5 rounded font-mono">
                        {relay.replace('wss://', '')}
                      </span>
                    ))}
                    {(connections.nostr?.relays?.length || 5) > 3 && (
                      <span className="text-xs text-muted-foreground">+{(connections.nostr?.relays?.length || 5) - 3} more</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Make.com Connection */}
            <div className={`border rounded-lg p-4 ${connections.makecom?.enabled ? 'border-blue-300 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20' : 'border-border'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Twitter className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">Make.com (X/Twitter)</span>
                </div>
                <Switch
                  checked={connections.makecom?.enabled}
                  onCheckedChange={(checked) => setConnections({
                    ...connections,
                    makecom: { ...connections.makecom, enabled: checked }
                  })}
                />
              </div>
              
              <div>
                <Label className="text-sm">Webhook URL</Label>
                <Input
                  type="url"
                  // Never show the masked placeholder in this visible field — when
                  // the stored value comes back masked, render blank so the
                  // "leave blank to keep" affordance reads true.
                  value={isMaskedSecret(connections.makecom?.webhook_url) ? '' : (connections.makecom?.webhook_url || '')}
                  onChange={(e) => setConnections({
                    ...connections,
                    makecom: { ...connections.makecom, webhook_url: e.target.value }
                  })}
                  placeholder={connections.makecom?.webhook_url_set ? 'Webhook set — leave blank to keep' : 'https://hook.us2.make.com/...'}
                  className="font-mono text-sm mt-1"
                />
                {connections.makecom?.webhook_url_set && (
                  <p className="text-xs text-muted-foreground mt-1">
                    A webhook URL is saved (hidden). Leave blank to keep it, or type a new URL to replace it.
                  </p>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <Button onClick={saveConnections}>Save Connections</Button>
          </div>
          </DialogContent>
        </Dialog>

        {/* Card Backgrounds Dialog */}
        <Dialog open={backgroundsDialog} onOpenChange={setBackgroundsDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Card Backgrounds</DialogTitle>
            </DialogHeader>
          <div className="flex items-center justify-between mb-4">
            <label className="cursor-pointer inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-[#E6830A] transition-colors">
              {bgUploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload to {bgCountry}{bgState ? ` / ${bgState}` : ' (National)'}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={bgUploading}
                onChange={(e) => { const fl = e.target.files && e.target.files[0]; e.target.value = ''; if (fl) uploadBackground(fl); }} />
            </label>
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            Cards render over the primary background for their scope. Resolution order: state &rarr; country (National) &rarr; AU National &rarr; bundled Outback road default. Upload to a scope, click a tile to make it primary, or delete the primary to revert that scope to its inherited image.
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-3">
            <label htmlFor="bg-country-select" className="text-sm text-muted-foreground">Country</label>
            <select id="bg-country-select" value={bgCountry}
              onChange={(e) => { const cc = e.target.value; setBgCountry(cc); setBgState(null); fetchBackgrounds(cc); }}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm">
              {BG_COUNTRIES.map(([code, name]) => (<option key={code} value={code}>{name}</option>))}
            </select>
          </div>

          {bgCountry === 'AU' && (
            <div className="flex flex-wrap gap-2 mb-4">
              {[['National', null]].concat(AU_STATES.map((st) => [st, st])).map(([label, code]) => {
                const sel = (bgState || null) === (code || null);
                const hasPrimary = backgrounds.some((b) => (code ? b.state_code === code : !b.state_code) && b.is_primary);
                return (
                  <button key={label} type="button" onClick={() => setBgState(code)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${sel ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}>
                    {label}{hasPrimary ? ' ●' : ''}
                  </button>
                );
              })}
            </div>
          )}

          {(() => {
            const scoped = backgrounds.filter((b) => (bgState ? b.state_code === bgState : !b.state_code));
            const hasPrimary = scoped.some((b) => b.is_primary);
            const inherit = bgState ? 'the National (AU) primary, then the bundled Outback road default' : 'the bundled Outback road default';
            return (
              <>
                {!hasPrimary && (
                  <div className="text-xs text-muted-foreground mb-3 italic">
                    No custom background set for {bgCountry}{bgState ? ` / ${bgState}` : ' (National)'} &mdash; cards here use {inherit}.
                  </div>
                )}
                {scoped.length === 0 ? (
                  <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground">
                    No images uploaded for this scope yet. Use &ldquo;Upload&rdquo; above.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {scoped.map((bg) => (
                      <div key={bg.id} className={`border rounded-lg overflow-hidden group relative ${bg.is_primary ? 'border-primary ring-2 ring-primary/30' : 'border-border'}`}>
                        <button type="button" title={bg.is_primary ? 'Primary background' : 'Set as primary'}
                          onClick={() => !bg.is_primary && setPrimaryBackground(bg.id)}
                          style={{ display: 'block', width: '100%', aspectRatio: '16/9', overflow: 'hidden' }} className="bg-muted">
                          <img src={bg.url} alt={bg.original_name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        </button>
                        <div className="flex items-center justify-between px-3 py-2 text-xs">
                          <span className="truncate" title={bg.original_name}>{bg.original_name}</span>
                          {bg.is_primary ? (
                            <span className="inline-flex items-center gap-1 text-primary font-medium"><Star className="w-3 h-3 fill-current" /> Primary</span>
                          ) : (
                            <button type="button" onClick={() => deleteBackground(bg.id)} className="text-muted-foreground hover:text-destructive p-1" title="Delete"><Trash2 className="w-3 h-3" /></button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
};

export default SocialMediaManagement;
